"use client";

import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, message, Modal } from "antd";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useEmergencyContact from "@/Profile/hooks/useEmergencyContact";
import "./InsuranceForm.css";

import {
  fetchInsuranceProviders,
  fetchUserInsurances,
  updateMedicalInfo,
} from "../../../../Profile/redux/thunks/medicalThunks";

import {
  selectInsuranceProviders,
  selectInsuranceProvidersLoading,
  selectUserInsurances,
  selectInsurancesSaving,
} from "../../../../Profile/redux/selectors/medicalSelectors";

import useAuthSession from "../../../../authPatient/hooks/useAuthSession";

/* Icons */
const Dot = () => <span className="insu-dot" aria-hidden />;

const PlusSq = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7.5v9M7.5 12h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Trash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M20 6L9 17l-5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const onEnter = (fn) => (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    fn();
  }
};

const normalizeId = (c) => c?._id || c?.id || null;

function Card({ title, children }) {
  return (
    <section className="insu-card">
      <header className="insu-card__header">
        <div className="insu-card__title">
          <Dot />
          <h3>{title}</h3>
        </div>
      </header>
      <div className="insu-card__body">{children}</div>
    </section>
  );
}

export default function InsuranceForm({ user }) {
  const { t } = useTranslation();
  const outletContext = useOutletContext() || {};
  const { registerSave } = outletContext;

  const dispatch = useDispatch();
  const { auth, effectiveUserId, effectiveUser } = useAuthSession({ auto: true });

  const currentUser = user || effectiveUser || auth?.user || null;
  const currentUserId = effectiveUserId || null;

  /* ─── INSURANCES ─── */
  const availableInsurances = useSelector(selectInsuranceProviders);
  const providersLoading = useSelector(selectInsuranceProvidersLoading);
  const storedInsurances = useSelector(selectUserInsurances);
  const insurancesSaving = useSelector(selectInsurancesSaving);

  const [selectedInsurances, setSelectedInsurances] = useState([]);

  useEffect(() => {
    dispatch(fetchInsuranceProviders());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    dispatch(fetchUserInsurances(currentUserId));
  }, [dispatch, currentUserId]);

  useEffect(() => {

    if (Array.isArray(storedInsurances)) {
      setSelectedInsurances(storedInsurances);
    } else {
      setSelectedInsurances([]);
    }
  }, [storedInsurances]);

  /* ─── CONTACTS CRUD ─── */
  const {
    contacts: apiContacts,
    status: contactsStatus,
    error: contactsError,
    userId,
    create,
    update,
    remove,
  } = useEmergencyContact();

  const [contacts, setContacts] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const lastSigRef = useRef("");

  useEffect(() => {
    const list = Array.isArray(apiContacts) ? apiContacts : [];
    const sig = list
      .map((c) => `${normalizeId(c)}:${c?.updatedAt || c?.createdAt || ""}`)
      .join("|");

    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;

    setContacts((prev) => {
      const tmpRows = prev.filter((x) => String(x._key || "").startsWith("tmp-"));

      const mapped = list.map((c) => {
        const id = normalizeId(c);
        const old = prev.find((p) => p._key === id);

        return {
          ...c,
          _key: id,
          editing: old?.editing ?? false,
          draftName: old?.draftName ?? c?.name ?? "",
          draftRelation: old?.draftRelation ?? c?.relation ?? "",
          draftPhone: old?.draftPhone ?? c?.phone ?? "",
          isNew: false,
        };
      });

      return [...mapped, ...tmpRows];
    });
  }, [apiContacts]);

  const patchDraft = useCallback((key, patch) => {
    setContacts((arr) => arr.map((x) => (x._key === key ? { ...x, ...patch } : x)));
  }, []);

  const startEdit = useCallback((key) => {
    patchDraft(key, { editing: true });
  }, [patchDraft]);

  const stopEdit = useCallback((key) => {
    patchDraft(key, { editing: false });
  }, [patchDraft]);

  const addContact = useCallback(() => {
    setContacts((arr) => {
      if (arr.length >= 5) {
        message.warning(t("insuranceForm.messages.maxContacts"));
        return arr;
      }

      const tmpKey = `tmp-${Date.now()}`;

      return [
        ...arr,
        {
          _key: tmpKey,
          editing: true,
          draftName: "",
          draftRelation: "",
          draftPhone: "",
          isNew: true,
        },
      ];
    });
  }, [t]);

  const commitContact = useCallback(
    async (key) => {
      const c = contacts.find((x) => x._key === key);
      if (!c) return;

      const name = String(c.draftName || "").trim();
      const phone = String(c.draftPhone || "").trim();
      const relation = String(c.draftRelation || "").trim();

      if (!name || !phone) {
        message.error(t("insuranceForm.messages.namePhoneRequired"));
        return;
      }

      try {
        setSavingKey(key);

        if (String(key).startsWith("tmp-")) {
          const res = await create({ name, phone, relation });
          if (res?.ok !== false) {
            setContacts((arr) => arr.filter((x) => x._key !== key));
          }
          return;
        }

        const realId = normalizeId(c);
        if (!realId) {
          message.error(t("insuranceForm.messages.invalidUpdateId"));
          return;
        }

        const res = await update(String(realId), { name, phone, relation });
        if (res?.ok !== false) {
          stopEdit(key);
        }
      } catch (err) {
        message.error(err?.message || t("insuranceForm.messages.saveFailed"));
      } finally {
        setSavingKey(null);
      }
    },
    [contacts, create, update, stopEdit, t]
  );

  const askDelete = useCallback(
    (key) => {
      const c = contacts.find((x) => x._key === key);

      if (!c) {
        message.error(t("insuranceForm.messages.contactNotFound"));
        return;
      }

      if (String(key).startsWith("tmp-")) {
        setContacts((arr) => arr.filter((x) => x._key !== key));
        return;
      }

      const realId = normalizeId(c);

      if (!realId) {
        message.error(t("insuranceForm.messages.invalidContactId"));
        return;
      }

      if (!userId) {
        message.error(t("insuranceForm.messages.userIdRequired"));
        return;
      }

      setConfirmTarget({
        key,
        realId: String(realId),
        name: c?.name || c?.draftName || t("insuranceForm.modal.fallbackContactName"),
      });
      setConfirmOpen(true);
    },
    [contacts, userId, t]
  );

  const confirmDelete = useCallback(async () => {
    if (!confirmTarget?.realId || !confirmTarget?.key) {
      setConfirmOpen(false);
      setConfirmTarget(null);
      return;
    }

    const { realId, key } = confirmTarget;

    try {
      setDeletingKey(key);

      const result = await remove(realId);

      if (result?.ok !== false) {
        setContacts((arr) => arr.filter((x) => x._key !== key));
        message.success(t("insuranceForm.messages.contactDeleted"));
      }
    } catch (err) {
      message.error(err?.message || t("insuranceForm.messages.deleteFailed"));
    } finally {
      setDeletingKey(null);
      setConfirmOpen(false);
      setConfirmTarget(null);
    }
  }, [confirmTarget, remove, t]);

  const cancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setConfirmTarget(null);
  }, []);

  /* ─── REGISTER SAVE (backend) ─── */
  useEffect(() => {
    if (!registerSave) return;

    registerSave(async () => {

      const targetUserId = currentUserId;

      if (!targetUserId) {
        message.error(t("insuranceForm.messages.saveFailed"));
        return false;
      }

      try {
        const result = await dispatch(
          updateMedicalInfo({
            userId: targetUserId,
            values: { insurance: selectedInsurances },
          })
        ).unwrap();


        if (Array.isArray(result?.insurance)) {
          setSelectedInsurances(result.insurance);
        }

        dispatch(fetchUserInsurances(targetUserId));

        message.success(t("insuranceForm.messages.saved"));
        return true;
      } catch (err) {
        message.error(err?.message || t("insuranceForm.messages.saveFailed"));
        return false;
      }
    });

    return () => registerSave(null);
  }, [registerSave, selectedInsurances, dispatch, currentUserId, t]);

  /* ─── MEMOS ─── */
  const insuranceOptions = useMemo(() => {
    return (availableInsurances || []).map((ins) => ({
      label: ins,
      value: ins,
    }));
  }, [availableInsurances]);

  const contactsCountText = useMemo(() => {
    return t("insuranceForm.contacts.counter", {
      count: contacts.length,
      max: 5,
    });
  }, [contacts.length, t]);

  return (
    <div className="insu-panel">
      <Modal
        open={confirmOpen}
        title={t("insuranceForm.modal.title")}
        okText={t("insuranceForm.modal.ok")}
        okType="danger"
        cancelText={t("insuranceForm.modal.cancel")}
        centered
        zIndex={4000}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        confirmLoading={!!confirmTarget && deletingKey === confirmTarget.key}
        destroyOnClose
      >
        <p style={{ marginBottom: 0 }}>
          {t("insuranceForm.modal.question")}{" "}
          <b>{confirmTarget?.name || t("insuranceForm.modal.fallbackContactName")}</b> ?
          <br />
          {t("insuranceForm.modal.warning")}
        </p>
      </Modal>

      <Card title={t("insuranceForm.cards.insurance.title")}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder={
            selectedInsurances?.length
              ? selectedInsurances.join(", ")
              : t("insuranceForm.cards.insurance.placeholder")
          }
          loading={providersLoading || insurancesSaving}
          value={selectedInsurances}
          onChange={setSelectedInsurances}
          options={insuranceOptions}
          style={{ width: "100%" }}
        />
      </Card>

      <Card title={t("insuranceForm.cards.contact.title")}>
        <div className="insu-two">
          <div className="insu-two__left">
            <p className="insu-help">{t("insuranceForm.cards.contact.leftHelp")}</p>

            {contactsStatus === "loading" && (
              <p className="insu-help" style={{ marginTop: 12 }}>
                {t("insuranceForm.cards.contact.loading")}
              </p>
            )}

            {contactsError && (
              <p className="insu-help" style={{ marginTop: 12, color: "crimson" }}>
                {String(contactsError)}
              </p>
            )}
          </div>

          <div className="insu-two__right">
            <div className="insu-contacts">
              {contacts.map((c) => {
                const key = c._key;
                const isSaving = savingKey === key;
                const isDeleting = deletingKey === key;
                const isDisabled = isSaving || isDeleting;

                const initials =
                  (c.draftName || c.name || " ")
                    .trim()
                    .split(" ")
                    .filter(Boolean)
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase() || "?";

                return (
                  <div key={key} className="insu-contact">
                    <div className="insu-contact__avatar" aria-hidden>
                      {initials}
                    </div>

                    <div className="insu-contact__info">
                      <div className="insu-contact__top">
                        {c.editing ? (
                          <>
                            <input
                              className="insu-input"
                              value={c.draftName}
                              onChange={(e) => patchDraft(key, { draftName: e.target.value })}
                              placeholder={t("insuranceForm.fields.fullName")}
                              disabled={isDisabled}
                              onKeyDown={onEnter(() => commitContact(key))}
                            />

                            <input
                              className="insu-input"
                              value={c.draftRelation}
                              onChange={(e) =>
                                patchDraft(key, { draftRelation: e.target.value })
                              }
                              placeholder={t("insuranceForm.fields.relation")}
                              disabled={isDisabled}
                              onKeyDown={onEnter(() => commitContact(key))}
                            />
                          </>
                        ) : (
                          <>
                            <span
                              className="insu-contact__name"
                              role="button"
                              tabIndex={0}
                              onClick={() => !isDisabled && startEdit(key)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") startEdit(key);
                              }}
                              style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                            >
                              {c.name || "—"}
                            </span>

                            <span className="insu-contact__relation">
                              ({c.relation || t("insuranceForm.fields.relationFallback")})
                            </span>
                          </>
                        )}
                      </div>

                      <div className="insu-contact__phone">
                        {c.editing ? (
                          <input
                            className="insu-input"
                            value={c.draftPhone}
                            onChange={(e) => patchDraft(key, { draftPhone: e.target.value })}
                            placeholder={t("insuranceForm.fields.phoneNumber")}
                            disabled={isDisabled}
                            onKeyDown={onEnter(() => commitContact(key))}
                          />
                        ) : (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => !isDisabled && startEdit(key)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") startEdit(key);
                            }}
                            style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                          >
                            {c.phone || "—"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="insu-contact__btns">
                      {c.editing && (
                        <button
                          type="button"
                          className="insu-iconbtn"
                          title={t("insuranceForm.actions.save")}
                          disabled={isDisabled}
                          onClick={() => commitContact(key)}
                        >
                          {isSaving ? "..." : <SaveIcon />}
                        </button>
                      )}

                      <button
                        type="button"
                        className="insu-iconbtn insu-iconbtn--danger"
                        title={t("insuranceForm.actions.delete")}
                        disabled={isDisabled}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          askDelete(key);
                        }}
                      >
                        {isDeleting ? "..." : <Trash />}
                      </button>
                    </div>
                  </div>
                );
              })}

              <button type="button" className="insu-addcard" onClick={addContact}>
                <span className="insu-addcard__icon">
                  <PlusSq />
                </span>

                <span>
                  <div className="insu-addcard__title">{t("insuranceForm.addCard.title")}</div>
                  <div className="insu-addcard__hint">{contactsCountText}</div>
                </span>
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
